import { useEffect, useState } from "react";
import { ArrowLeft, Edit, FolderTree, Plus, Save, Trash2, X } from "lucide-react";
import { Link } from "react-router-dom";
import api from "../../../api/api";

const emptyForm = {
  name: "",
  description: "",
};

export default function AdminAnnouncementCategories() {
  const [categories, setCategories] = useState([]);
  const [form, setForm] = useState(emptyForm);
  const [editingId, setEditingId] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  const fetchCategories = async () => {
    setLoading(true);

    try {
      const res = await api.get("/announcement-categories");
      setCategories(res.data || []);
    } catch (error) {
      console.error("Failed to fetch announcement categories:", error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchCategories();
  }, []);

  const resetForm = () => {
    setForm(emptyForm);
    setEditingId(null);
  };

  const startEdit = (category) => {
    setEditingId(category.id);
    setForm({
      name: category.name || "",
      description: category.description || "",
    });
  };

  const submitCategory = async (e) => {
    e.preventDefault();

    if (!form.name.trim()) {
      alert("Category name is required.");
      return;
    }

    setSaving(true);

    try {
      const payload = {
        name: form.name.trim(),
        description: form.description.trim(),
      };

      if (editingId) {
        await api.put(`/announcement-categories/${editingId}`, payload);
      } else {
        await api.post("/announcement-categories", payload);
      }

      resetForm();
      fetchCategories();
    } catch (error) {
      console.error("Failed to save category:", error);
      alert(
        error?.response?.data?.detail ||
          "Failed to save announcement category."
      );
    } finally {
      setSaving(false);
    }
  };

  const deleteCategory = async (categoryId) => {
    const confirmed = window.confirm(
      "Are you sure you want to delete this announcement category?"
    );

    if (!confirmed) return;

    try {
      await api.delete(`/announcement-categories/${categoryId}`);
      fetchCategories();
    } catch (error) {
      console.error("Failed to delete category:", error);
      alert(
        error?.response?.data?.detail ||
          "Failed to delete category. It may already be attached to announcements."
      );
    }
  };

  return (
    <div className="space-y-6">
      <div>
        <Link
          to="/admin/announcements"
          className="inline-flex items-center gap-2 text-sm font-bold text-slate-500 hover:text-[#061A40]"
        >
          <ArrowLeft size={17} />
          Back to announcements
        </Link>

        <h1 className="text-2xl md:text-3xl font-black text-[#061A40] mt-3">
          Announcement Categories
        </h1>

        <p className="text-sm text-slate-500 mt-1">
          Create and manage categories for official announcements.
        </p>
      </div>

      <div className="grid xl:grid-cols-[420px_1fr] gap-6">
        <form
          onSubmit={submitCategory}
          className="bg-white rounded-3xl border border-slate-200 shadow-sm p-6 h-fit"
        >
          <div className="flex items-center gap-3 mb-6">
            <div className="w-11 h-11 rounded-2xl bg-[#F4B000]/15 text-[#061A40] flex items-center justify-center">
              {editingId ? <Edit size={22} /> : <Plus size={22} />}
            </div>

            <div>
              <h2 className="font-black text-[#061A40]">
                {editingId ? "Edit Category" : "New Category"}
              </h2>

              <p className="text-xs text-slate-500">
                Categories help organize announcements.
              </p>
            </div>
          </div>

          <div className="space-y-5">
            <div>
              <label className="block text-sm font-bold text-slate-700 mb-2">
                Category Name
              </label>

              <input
                value={form.name}
                onChange={(e) =>
                  setForm((prev) => ({
                    ...prev,
                    name: e.target.value,
                  }))
                }
                placeholder="e.g. Public Notice"
                className="w-full px-4 py-3 rounded-2xl border border-slate-200 outline-none focus:border-[#061A40]"
              />
            </div>

            <div>
              <label className="block text-sm font-bold text-slate-700 mb-2">
                Description
              </label>

              <textarea
                value={form.description}
                onChange={(e) =>
                  setForm((prev) => ({
                    ...prev,
                    description: e.target.value,
                  }))
                }
                rows={5}
                placeholder="Short category description"
                className="w-full px-4 py-3 rounded-2xl border border-slate-200 outline-none focus:border-[#061A40]"
              />
            </div>

            <div className="flex gap-3">
              <button
                type="submit"
                disabled={saving}
                className="flex-1 inline-flex items-center justify-center gap-2 px-5 py-3 rounded-2xl bg-[#061A40] text-white font-black hover:bg-[#0A2A62] disabled:opacity-60"
              >
                <Save size={18} />
                {saving ? "Saving..." : editingId ? "Update" : "Create"}
              </button>

              {editingId && (
                <button
                  type="button"
                  onClick={resetForm}
                  className="inline-flex items-center justify-center px-5 py-3 rounded-2xl bg-slate-100 text-slate-700 font-black hover:bg-slate-200"
                >
                  <X size={18} />
                </button>
              )}
            </div>
          </div>
        </form>

        <section className="bg-white rounded-3xl border border-slate-200 shadow-sm overflow-hidden">
          {loading ? (
            <div className="p-10 text-center text-slate-500 font-bold">
              Loading categories...
            </div>
          ) : categories.length === 0 ? (
            <div className="p-16 text-center">
              <FolderTree size={48} className="mx-auto text-slate-300" />

              <h2 className="text-xl font-black text-[#061A40] mt-4">
                No categories yet
              </h2>

              <p className="text-slate-500 mt-2">
                Add your first announcement category.
              </p>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-left">
                <thead className="bg-slate-50 border-b border-slate-200">
                  <tr>
                    <th className="px-5 py-4 text-xs uppercase tracking-widest text-slate-500">
                      Name
                    </th>
                    <th className="px-5 py-4 text-xs uppercase tracking-widest text-slate-500">
                      Slug
                    </th>
                    <th className="px-5 py-4 text-xs uppercase tracking-widest text-slate-500">
                      Description
                    </th>
                    <th className="px-5 py-4 text-xs uppercase tracking-widest text-slate-500 text-right">
                      Action
                    </th>
                  </tr>
                </thead>

                <tbody className="divide-y divide-slate-100">
                  {categories.map((category) => (
                    <tr key={category.id} className="hover:bg-slate-50">
                      <td className="px-5 py-4 font-black text-[#061A40]">
                        {category.name}
                      </td>

                      <td className="px-5 py-4 text-sm text-slate-500">
                        {category.slug}
                      </td>

                      <td className="px-5 py-4 text-sm text-slate-600 max-w-md">
                        {category.description || "No description"}
                      </td>

                      <td className="px-5 py-4">
                        <div className="flex justify-end gap-2">
                          <button
                            type="button"
                            onClick={() => startEdit(category)}
                            className="w-10 h-10 rounded-xl bg-slate-100 text-[#061A40] hover:bg-[#061A40] hover:text-white flex items-center justify-center"
                          >
                            <Edit size={17} />
                          </button>

                          <button
                            type="button"
                            onClick={() => deleteCategory(category.id)}
                            className="w-10 h-10 rounded-xl bg-red-50 text-red-600 hover:bg-red-600 hover:text-white flex items-center justify-center"
                          >
                            <Trash2 size={17} />
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </section>
      </div>
    </div>
  );
}